import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { BaseBatteryStatus, BaseChildConnectType, BaseChildReport } from '../../../../types/base'

/**
 * @file command-io.ts
 * @description 底座公共命令收发工具，只处理底座命令如何通过 HID 发送和校验 ACK，不持有任何具体芯片命令表。
 */

export type BaseProtocolConfig = {
  reportId: number
  featureReportId: number
  busyAck: number
  okAck: number
}

export type BaseCommandBytes = readonly number[]

/** 将 DataView 安全转换为只包含当前视图范围的 Uint8Array。 */
export function toBytes(data: DataView): Uint8Array {
  return new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
}

/**
 * 底座协议 ACK 统一校验。
 * 1.0 里的 ACKParser 只判断首字节是否为 0x01，这里保留同样语义，并把错误收敛成 SDK DeviceError。
 */
export function checkBaseACK(data: DataView, config: BaseProtocolConfig): void {
  const ack = data.getUint8(0)
  if (ack !== config.okAck) {
    throw new DeviceError(
      DeviceErrorCode.READ_FAILED,
      `Base ACK Error: Expected 0x${config.okAck.toString(16)}, got 0x${ack.toString(16)}`,
    )
  }
}

export async function sendBaseCommand(
  device: BaseDevice,
  config: BaseProtocolConfig,
  command: BaseCommandBytes,
  data: readonly number[] = [],
): Promise<DataView> {
  // 底座是“裸命令”协议，不需要像鼠标/键盘那样追加 0xff/0xa5 连接类型前缀。
  const payload = new Uint8Array([...command, ...data])

  const response = await device.sendAndWait(
    config.reportId,
    payload,
    config.featureReportId,
    (res) => res.getUint8(0) !== config.busyAck,
  )

  checkBaseACK(response, config)
  return response
}

/**
 * 发送已经完整拼好的底座 payload。
 *
 * 普通底座命令建议用 sendBaseCommand；只有 0xd0/0xd1/0xd2 等扩展命令，
 * 需要调用方自己控制长度字节、子命令字和参数时才使用这个方法。
 */
export async function sendBaseRaw(
  device: BaseDevice,
  config: BaseProtocolConfig,
  data: readonly number[],
): Promise<DataView> {
  // sendBaseRaw 用于 0xd0/0xd1/0xd2 这类屏幕或频段扩展命令，调用方负责拼完整 payload。
  const response = await device.sendAndWait(
    config.reportId,
    new Uint8Array(data),
    config.featureReportId,
    (res) => res.getUint8(0) !== config.busyAck,
  )

  checkBaseACK(response, config)
  return response
}

/** 从 feature report 指定范围读取 UTF-8 字符串，遇到 0x00 或 0xff 停止。 */
export function readUtf8UntilFill(data: DataView, start: number, endInclusive: number): string {
  const bytes: number[] = []
  for (let index = start; index <= endInclusive && index < data.byteLength; index++) {
    const byte = data.getUint8(index)
    // 固件未写入名称时常用 0xff 填充；字符串本身仍以 0x00 结尾。
    if (byte === 0x00 || byte === 0xff) break
    bytes.push(byte)
  }
  return new TextDecoder('utf-8').decode(new Uint8Array(bytes))
}

/**
 * 解析底座主动上报的子鼠标状态。
 *
 * @param buffer 原始 input report payload。
 * @param offset 协议数据起始偏移：Telink 为 0，VT_nrf54L_Base 为 1。
 */
export function parseBaseChildReport(buffer: Uint8Array, offset: 0 | 1): BaseChildReport {
  // Telink 主动上报从 byte0 开始；VT_nrf54L_Base 的 0xb0 子鼠标数据从 byte1 开始。
  const read = (index: number) => buffer[index + offset] ?? 0
  const mode = read(0)
  const connectType = convertConnectType(mode & 0x0f)
  const sensorTypeModel = (mode & 0xf0) >> 4
  const dpiX = read(2) | (read(3) << 8)
  const dpiY = read(4) | (read(5) << 8)

  return {
    mode: {
      connectType,
      sensorTypeModel,
    },
    dpi: {
      gear: read(1),
      dpiX,
      dpiY,
    },
    battery: {
      status: convertBatteryStatus(read(6)),
      battery: read(7),
    },
    blMode: read(8),
    rpt: {
      rpt24g: read(9),
      rptUsb: read(10),
    },
    config: read(11),
    raw: buffer,
  }
}

/** 将固件连接方式 nibble 转为 SDK 可读枚举。 */
function convertConnectType(value: number): BaseChildConnectType {
  switch (value) {
    case 0:
      return 'wirelessReceiver'
    case 1:
      return 'bluetooth'
    case 2:
      return 'usb'
    default:
      return 'unknown'
  }
}

/** 将固件电池状态字节转为 SDK 可读枚举。 */
function convertBatteryStatus(value: number): BaseBatteryStatus {
  switch (value) {
    case 1:
      return 'valid'
    case 2:
      return 'charging'
    default:
      return 'invalid'
  }
}
